/**
 * Inspector for **Look up in Veeva**.
 *
 * Two halves, in the order a person thinks about them: what identifies the
 * record (my column ↔ their field), then what to bring back.
 *
 * The prefix is the part worth defending. A source and a target both having a
 * `name__v` is the norm, not the exception, so looked-up columns are prefixed
 * by default — without it a lookup silently overwrites the row it was meant to
 * enrich, and the mistake only surfaces after the load.
 */

import { useEffect, useState } from "react";
import { Alert, Checkbox, MultiSelect, Select, Stack, Text, TextInput } from "@mantine/core";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { listConnections, type ConnectionOut } from "@/shared/api/integration";
import {
  veevaDescribeObject,
  veevaListObjects,
  type FieldDefinition,
  type ObjectSummary,
} from "@/shared/api/integration-browse";
import { toastApiError } from "@/shared/ui/toast";

import { ConnectionRefSelect } from "./ConnectionRefSelect";
import { useConnectionRef } from "./useConnectionRef";

export function LookupConfig({
  config,
  editable,
  onChange,
  inputColumns,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  inputColumns: Record<string, string[]>;
}) {
  const getToken = useAccessToken();
  const [connections, setConnections] = useState<ConnectionOut[]>([]);
  const [objects, setObjects] = useState<ObjectSummary[]>([]);
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  // The node names a connection id or a `@role`; metadata takes an id.
  const {
    connectionId,
    role,
    settled: roleSettled,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const object = String(config.object ?? "");
  const incoming = inputColumns["in-0"] ?? [];
  const bringBack = Array.isArray(config.fields) ? config.fields.map(String) : [];
  const prefix = String(config.prefix ?? "");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const items = (await listConnections(await getToken())).items;
        if (!cancelled) setConnections(items.filter((c) => c.source_type === "veeva"));
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (!connectionId) return;
    let cancelled = false;
    void (async () => {
      try {
        const items = (await veevaListObjects(await getToken(), connectionId)).items;
        if (!cancelled) setObjects(items);
      } catch (err) {
        toastApiError("Could not load the vault's objects", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  useEffect(() => {
    if (!connectionId || !object) {
      setFields([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const found = (await veevaDescribeObject(await getToken(), connectionId, object)).fields;
        if (!cancelled) setFields(found);
      } catch (err) {
        toastApiError("Could not load that object's fields", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, object]);

  const fieldNames = fields.map((f) => f.name);
  const collides = bringBack.some((f) => incoming.includes(`${prefix}${f}`));

  return (
    <Stack gap="sm">
      <ConnectionRefSelect
        label="Vault to look in"
        placeholder={connections.length ? "Pick a connection" : "No Veeva connections set up"}
        data={connections.map((c) => ({ value: c.connection_id, label: c.display_name }))}
        connectionId={connectionId}
        role={role}
        settled={roleSettled}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.connection_id === v)?.display_name ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          onChange("object", "");
          onChange("matchField", "");
          onChange("fields", []);
        }}
      />
      <Select
        label="Object to find"
        description="The records being referenced — products, countries, studies."
        placeholder={connectionId ? "Pick an object" : "Pick a connection first"}
        data={objects.map((o) => ({ value: o.name, label: o.label ?? o.name }))}
        value={object || null}
        onChange={(v) => onChange("object", v ?? "")}
        disabled={!editable || !connectionId}
        searchable
      />

      <Select
        label="My column"
        description="The value your rows carry — a name, or the source system's own id."
        placeholder={incoming.length ? "Pick a column" : "Test the source to load columns"}
        data={Array.from(new Set([...incoming, String(config.column ?? "")].filter(Boolean)))}
        value={String(config.column ?? "") || null}
        onChange={(v) => onChange("column", v ?? "")}
        disabled={!editable}
        searchable
      />
      <Select
        label="Their field"
        description="The Veeva field to match it against — external_id__v for a migrated id, name__v for a name."
        placeholder={object ? "Pick a field" : "Pick an object first"}
        data={Array.from(new Set([...fieldNames, String(config.matchField ?? "")].filter(Boolean)))}
        value={String(config.matchField ?? "") || null}
        onChange={(v) => onChange("matchField", v ?? "")}
        disabled={!editable || !object}
        searchable
      />

      <MultiSelect
        label="Bring back"
        description="The record's id always comes back — that is what a reference needs. Add any other fields you want on the row."
        data={Array.from(new Set([...fieldNames, ...bringBack]))}
        value={bringBack}
        onChange={(v) => onChange("fields", v)}
        disabled={!editable || !object}
        searchable
        clearable
      />
      <TextInput
        label="Prefix the columns with"
        description="So a looked-up name__v does not overwrite your own. Clear it only if you mean to replace."
        value={prefix}
        onChange={(e) => onChange("prefix", e.currentTarget.value)}
        disabled={!editable}
      />
      {!prefix && (
        <Alert color="orange" variant="light">
          With no prefix, a looked-up column replaces one of the same name on your row — and the
          mistake only shows up after the load.
        </Alert>
      )}
      {collides && (
        <Alert color="orange" variant="light">
          A column of that name already arrives here. Change the prefix, or the lookup will
          overwrite it.
        </Alert>
      )}

      <Checkbox
        label="Stop the run if any row finds nothing"
        description="Off by default: a row with no match is work, not an error. It leaves by the second output with the reason, so the rest can still load."
        checked={Boolean(config.failIfMissing)}
        onChange={(e) => onChange("failIfMissing", e.currentTarget.checked)}
        disabled={!editable}
      />

      <Text size="xs" c="dimmed">
        Two outputs: <b>found</b> carries your rows plus the looked-up columns, <b>not found</b>{" "}
        carries the rest with{" "}
        <Text span ff="monospace">
          _lookup
        </Text>{" "}
        saying what was searched for.
      </Text>
    </Stack>
  );
}
